import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Form, Spinner, Button, Badge, Alert, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const formatCurrency = (n) =>
  parseFloat(n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const Monto = ({ valor, fuerte, color = 'danger' }) =>
  valor > 0.009
    ? <span className={`text-${color} ${fuerte ? 'fw-bold' : ''}`}>{formatCurrency(valor)}</span>
    : <span className="text-muted">—</span>;

const ReporteDeudas = () => {
  const navigate = useNavigate();
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reglas, setReglas] = useState({ cuotaAporte: 10, desde: '2025-10', cuotaPaseo: 5, desdePaseo: '2026-08', cuotaPrograma: 0 });
  // Vacío al inicio: el servidor usa las reglas guardadas en Configuración
  const [reglasAplicadas, setReglasAplicadas] = useState({});
  const [busqueda, setBusqueda] = useState('');
  const [soloConDeuda, setSoloConDeuda] = useState(false);
  const [expandido, setExpandido] = useState(null);
  const [guardandoExento, setGuardandoExento] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(Object.entries(reglasAplicadas).map(([k, v]) => [k, String(v)]));
      const res = await axios.get(`/api/reportes-prestamos/deudas?${params.toString()}`);
      setDatos(res.data);
      setReglas(res.data.reglas);
    } catch (error) {
      console.error('Error al cargar el reporte de deudas:', error);
      toast.error('Error al cargar el reporte de deudas');
    } finally {
      setLoading(false);
    }
  }, [reglasAplicadas]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarRegla = (campo, valor) => setReglas(prev => ({ ...prev, [campo]: valor }));
  const aplicar = (e) => { e.preventDefault(); setReglasAplicadas(reglas); };

  // Guarda cuota y mes de inicio como predeterminados (también los usa el
  // aviso de meses pendientes en "Registrar Valores")
  const guardarReglas = async () => {
    try {
      const res = await axios.put('/api/reportes-prestamos/deudas/reglas', { cuotaAporte: reglas.cuotaAporte, desde: reglas.desde, cuotaPaseo: reglas.cuotaPaseo, desdePaseo: reglas.desdePaseo });
      toast.success(res.data.message);
      setReglasAplicadas({ ...reglas });
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudieron guardar las reglas');
    }
  };

  // Exento de aportes: se guarda en el perfil del socio (vale para todos)
  const alternarExento = async (fila) => {
    try {
      setGuardandoExento(fila.socioId);
      const res = await axios.patch(`/api/reportes-prestamos/deudas/exento/${fila.socioId}`, { exentoAportes: !fila.exentoAportes });
      toast.success(res.data.message);
      await cargar();
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo actualizar el socio');
    } finally {
      setGuardandoExento(null);
    }
  };

  const conPaseo = (datos?.reglas?.cuotaPaseo || 0) > 0;
  const conPrograma = (datos?.reglas?.cuotaPrograma || 0) > 0;

  // Totales recalculados sobre las filas
  const incluidas = useMemo(() => [...(datos?.data || [])]
    .sort((a, b) => String(a.numeroSocio || '').localeCompare(String(b.numeroSocio || ''), 'es', { numeric: true })), [datos]);
  const totales = useMemo(() => {
    const s = (fn) => Math.round(incluidas.reduce((a, f) => a + fn(f), 0) * 100) / 100;
    return {
      aportes: s(f => f.aportes.adeudado),
      paseo: s(f => f.paseo.adeudado),
      programa: s(f => f.programa.adeudado),
      prestamoMonto: s(f => f.prestamoMonto),
      paseoPagado: s(f => f.pagado.paseo),
      programaPagado: s(f => f.pagado.programa),
      prestamoSaldo: s(f => f.prestamoSaldo),
      multas: s(f => f.multasPendientes),
      totalAdeudado: s(f => f.totalAdeudado),
      sociosConDeuda: incluidas.filter(f => f.totalAdeudado > 0.009).length
    };
  }, [incluidas]);

  const visibles = useMemo(() => {
    // Busca cada palabra por separado y sin tildes: "jenny castellanos"
    // encuentra a "CASTELLANOS IZURIETA JENNY".
    const norm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const palabras = norm(busqueda).split(/\s+/).filter(Boolean);
    return incluidas.filter(f => {
      const texto = norm(`${f.socio} ${f.cedula} ${f.numeroSocio}`);
      if (palabras.some(p => !texto.includes(p))) return false;
      if (soloConDeuda && !(f.totalAdeudado > 0.009)) return false;
      return true;
    });
  }, [incluidas, busqueda, soloConDeuda]);

  const exportarCSV = () => {
    const mes = datos ? new Date(datos.fechaCorte).toLocaleDateString('es-ES') : '';
    const cab = ['#', 'Socio', 'Cédula', 'Nº socio', 'Aportes adeudados', 'Meses de aporte'];
    if (conPaseo) cab.push('Paseo adeudado');
    if (conPrograma) cab.push('Programa adeudado');
    cab.push('Préstamo (monto)', 'Debe del préstamo hoy', 'Multas pendientes', 'TOTAL QUE DEBE HOY', 'Paseo pagado', 'Programa pagado');
    const filas = [[`Reporte de deudas al ${mes} — cuota aporte $${datos.reglas.cuotaAporte} desde ${datos.reglas.desde}`], cab];
    incluidas.forEach((f, i) => {
      const fila = [i + 1, f.socio, f.cedula, f.numeroSocio, f.aportes.adeudado, f.aportes.meses.map(m => m.mes).join(' ')];
      if (conPaseo) fila.push(f.paseo.adeudado);
      if (conPrograma) fila.push(f.programa.adeudado);
      fila.push(f.prestamoMonto, f.prestamoSaldo, f.multasPendientes, f.totalAdeudado, f.pagado.paseo, f.pagado.programa);
      filas.push(fila);
    });
    const tot = ['', `TOTAL (${incluidas.length} socios)`, '', '', totales.aportes, ''];
    if (conPaseo) tot.push(totales.paseo);
    if (conPrograma) tot.push(totales.programa);
    tot.push(totales.prestamoMonto, totales.prestamoSaldo, totales.multas, totales.totalAdeudado, totales.paseoPagado, totales.programaPagado);
    filas.push([], tot);

    const csv = filas.map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-deudas-${mes.replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columnas = 9 + (conPaseo ? 1 : 0) + (conPrograma ? 1 : 0);
  const fechaCorte = datos ? new Date(datos.fechaCorte) : null;
  const mesCorte = fechaCorte ? fechaCorte.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }) : '';

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <h2 className="text-gradient mb-2">
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  Reporte de Deudas
                </h2>
                <p className="text-muted mb-0">
                  Cuánto debe cada socio hasta {mesCorte || 'hoy'} (incluye el mes actual): aportes, préstamos y multas.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Reglas de cálculo */}
        <Card className="card-custom mb-4">
          <Card.Body>
            <Form onSubmit={aplicar}>
              <Row className="g-3 align-items-end">
                <Col md={2} sm={6}>
                  <Form.Label className="small mb-1">Cuota de aporte / mes</Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control type="number" min="0" step="0.01" value={reglas.cuotaAporte}
                      onChange={(e) => cambiarRegla('cuotaAporte', e.target.value)} />
                  </InputGroup>
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="small mb-1">Contar desde</Form.Label>
                  <Form.Control size="sm" type="month" value={reglas.desde}
                    onChange={(e) => cambiarRegla('desde', e.target.value)} />
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="small mb-1">Cuota de paseo / mes</Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control type="number" min="0" step="0.01" value={reglas.cuotaPaseo}
                      onChange={(e) => cambiarRegla('cuotaPaseo', e.target.value)} />
                  </InputGroup>
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="small mb-1">Paseo desde</Form.Label>
                  <Form.Control size="sm" type="month" value={reglas.desdePaseo || ''}
                    onChange={(e) => cambiarRegla('desdePaseo', e.target.value)} />
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="small mb-1">Cuota de programa / mes</Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control type="number" min="0" step="0.01" value={reglas.cuotaPrograma}
                      onChange={(e) => cambiarRegla('cuotaPrograma', e.target.value)} />
                  </InputGroup>
                </Col>
                <Col md={12} className="d-flex gap-2">
                  <Button type="submit" size="sm" variant="primary" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <><i className="fas fa-sync me-1"></i>Recalcular</>}
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={guardarReglas} title="Usar esta cuota y mes de inicio por defecto, también al registrar valores">
                    <i className="fas fa-save me-1"></i>Guardar cuotas
                  </Button>
                  <Button size="sm" variant="success" onClick={exportarCSV} disabled={!datos}>
                    <i className="fas fa-file-excel me-1"></i>Exportar
                  </Button>
                </Col>
              </Row>
              <small className="text-muted d-block mt-2">
                Una cuota en $0 no se cobra. Lo pagado de más en un mes cuenta como adelanto para los siguientes,
                salvo el primer mes de cada socio (trae el saldo histórico con el que arrancó el sistema).
              </small>
            </Form>
          </Card.Body>
        </Card>

        {loading && !datos ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : !datos ? null : (
          <>
            <Row className="mb-4">
              <Col lg={3} md={6} className="mb-3">
                <Card className="stats-card border-danger h-100">
                  <Card.Body>
                    <div className="stats-icon text-danger"><i className="fas fa-exclamation-circle"></i></div>
                    <div className="stats-number text-danger">{formatCurrency(totales.totalAdeudado)}</div>
                    <div className="stats-label">Total que deben hoy</div>
                    <small className="text-muted">{totales.sociosConDeuda} socio(s) con deuda</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <Card className="stats-card border-warning h-100">
                  <Card.Body>
                    <div className="stats-icon text-warning"><i className="fas fa-hand-holding-usd"></i></div>
                    <div className="stats-number text-warning">{formatCurrency(totales.aportes + totales.paseo + totales.programa)}</div>
                    <div className="stats-label">Aportes{conPaseo || conPrograma ? ' y cuotas' : ''} adeudados</div>
                    <small className="text-muted">a {formatCurrency(datos.reglas.cuotaAporte)} por mes desde {datos.reglas.desde}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <Card className="stats-card border-primary h-100">
                  <Card.Body>
                    <div className="stats-icon text-primary"><i className="fas fa-file-invoice"></i></div>
                    <div className="stats-number text-primary">{formatCurrency(totales.prestamoSaldo)}</div>
                    <div className="stats-label">Deben de préstamos hoy</div>
                    <small className="text-muted">de {formatCurrency(totales.prestamoMonto)} prestados (préstamos activos)</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <Card className="stats-card border-secondary h-100">
                  <Card.Body>
                    <div className="stats-icon text-secondary"><i className="fas fa-gavel"></i></div>
                    <div className="stats-number text-secondary">{formatCurrency(totales.multas)}</div>
                    <div className="stats-label">Multas pendientes</div>
                    <small className="text-muted">Ya aplicadas y sin pagar</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Para regularizar: deben aportes y pagaron de más en otro mes */}
            {(() => {
              const porRegularizar = (datos.data || []).filter(f => f.regularizar);
              if (porRegularizar.length === 0) return null;
              const abrirRegistro = (f, e) =>
                navigate(`/socios/valores?socio=${f.socioId}&mes=${e.numeroMes}&año=${e.año}`);
              return (
                <Card className="card-custom mb-4 border-warning">
                  <Card.Header className="bg-warning bg-opacity-25">
                    <h5 className="mb-0">
                      <i className="fas fa-random me-2"></i>
                      Para regularizar ({porRegularizar.length})
                    </h5>
                    <small className="text-muted">
                      Deben aporte de algún mes y después pagaron de más. Abre el mes donde pagó de más y marca qué meses cubre.
                    </small>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table size="sm" hover className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Socio</th>
                            <th>Debe aporte de</th>
                            <th>Pagó de más en</th>
                          </tr>
                        </thead>
                        <tbody>
                          {porRegularizar.map(f => (
                            <tr key={f.socioId}>
                              <td className="fw-bold">{f.socio}</td>
                              <td>
                                {f.aportes.meses.map(m => (
                                  <Badge key={m.mes} bg="danger" className="me-1 mb-1">{m.mes} · falta {formatCurrency(m.falta)}</Badge>
                                ))}
                              </td>
                              <td>
                                {f.regularizar.excedentes.map(e => (
                                  <Button key={e.mes} size="sm" variant="outline-success" className="me-1 mb-1"
                                    onClick={() => abrirRegistro(f, e)}
                                    title={`Abrir el registro de ${e.mes} para asignar el excedente`}>
                                    {e.mes}: aportó {formatCurrency(e.aporte)} (+{formatCurrency(e.excedente)})
                                    <i className="fas fa-external-link-alt ms-1"></i>
                                  </Button>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              );
            })()}

            <Card className="card-custom">
              <Card.Header className="card-header-custom d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <h5 className="mb-0"><i className="fas fa-users me-2"></i>Deudas por socio</h5>
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <Form.Control size="sm" placeholder="Buscar socio o cédula..." value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)} style={{ maxWidth: 220 }} />
                  <Form.Check type="switch" id="solo-deuda" label="Solo con deuda" className="text-white"
                    checked={soloConDeuda} onChange={(e) => setSoloConDeuda(e.target.checked)} />
                  <small className="text-white-50">Mostrando {visibles.length} de {incluidas.length} socios</small>
                </div>
              </Card.Header>
              <Card.Body>
                {visibles.length === 0 ? (
                  <Alert variant="success" className="mb-0">Ningún socio tiene deudas con estos filtros.</Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th className="text-center">#</th>
                          <th>Socio</th>
                          <th className="text-end">Aportes</th>
                          {conPaseo && <th className="text-end">Paseo</th>}
                          {conPrograma && <th className="text-end">Programa</th>}
                          <th className="text-end">Préstamo</th>
                          <th className="text-end">Debe del préstamo hoy</th>
                          <th className="text-end">Multas</th>
                          <th className="text-end">Total que debe hoy</th>
                          <th className="text-end text-success border-start">Paseo pagado</th>
                          <th className="text-end text-success">Programa pagado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibles.map((f, i) => {
                          const abierto = expandido === f.socioId;
                          return (
                            <React.Fragment key={f.socioId}>
                              <tr style={{ cursor: 'pointer' }}
                                onClick={() => setExpandido(abierto ? null : f.socioId)}>
                                <td className="text-center fw-bold text-muted">{i + 1}</td>
                                <td>
                                  <i className={`fas fa-chevron-${abierto ? 'down' : 'right'} me-2 text-muted`}></i>
                                  <span className="fw-bold">{f.socio}</span>
                                  {f.exentoAportes && <Badge bg="info" className="ms-2">exento de aportes</Badge>}
                                  <div><small className="text-muted ms-4">{f.numeroSocio}{f.cedula ? ` · ${f.cedula}` : ''}</small></div>
                                </td>
                                <td className="text-end">
                                  {f.exentoAportes ? <small className="text-info">exento</small> : <Monto valor={f.aportes.adeudado} />}
                                  {f.aportes.meses.length > 0 && <div><small className="text-muted">{f.aportes.meses.length} mes(es)</small></div>}
                                </td>
                                {conPaseo && <td className="text-end"><Monto valor={f.paseo.adeudado} /></td>}
                                {conPrograma && <td className="text-end"><Monto valor={f.programa.adeudado} /></td>}
                                <td className="text-end">
                                  <Monto valor={f.prestamoMonto} color="dark" />
                                  {f.prestamos.length > 1 && <div><small className="text-muted">{f.prestamos.length} préstamos</small></div>}
                                </td>
                                <td className="text-end"><Monto valor={f.prestamoSaldo} /></td>
                                <td className="text-end"><Monto valor={f.multasPendientes} /></td>
                                <td className="text-end"><Monto valor={f.totalAdeudado} fuerte /></td>
                                <td className="text-end border-start"><Monto valor={f.pagado.paseo} color="success" /></td>
                                <td className="text-end"><Monto valor={f.pagado.programa} color="success" /></td>
                              </tr>
                              {abierto && (
                                <tr className="table-light">
                                  <td colSpan={columnas}>
                                    <Row className="g-3 py-2">
                                      <Col md={4}>
                                        <h6 className="mb-2"><i className="fas fa-hand-holding-usd me-1 text-warning"></i>Aportes (desde {f.desde})</h6>
                                        {f.exentoAportes
                                          ? <small className="text-info">Exento: no paga aportes</small>
                                          : f.aportes.meses.length === 0
                                          ? <small className="text-success">Al día</small>
                                          : <ul className="small mb-0 ps-3">{f.aportes.meses.map(m => <li key={m.mes}>{m.mes}: falta {formatCurrency(m.falta)}</li>)}</ul>}
                                        {!f.exentoAportes && (f.aportes.cubiertosDespues || []).length > 0 && (
                                          <div className="small text-success mt-2">
                                            {f.aportes.cubiertosDespues.map(c => (
                                              <div key={c.mes}>
                                                <i className="fas fa-check me-1"></i>
                                                {c.mes} cubierto con el pago de {c.con.map(x => `${x.desde} (${formatCurrency(x.monto)})`).join(', ')}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <h6 className="mt-3 mb-2"><i className="fas fa-bus me-1 text-warning"></i>Paseo</h6>
                                        {conPaseo && (f.paseo.meses.length === 0
                                          ? <small className="text-success d-block">Al día</small>
                                          : <ul className="small mb-1 ps-3">{f.paseo.meses.map(m => <li key={m.mes} className="text-danger">{m.mes}: falta {formatCurrency(m.falta)}</li>)}</ul>)}
                                        <small className="text-muted d-block">
                                          Pagado: {f.pagado.detallePaseo.length === 0 ? 'nada' : f.pagado.detallePaseo.map(x => `${x.mes} ${formatCurrency(x.monto)}`).join(' · ')}
                                        </small>
                                        <h6 className="mt-3 mb-2"><i className="fas fa-star me-1 text-info"></i>Programa</h6>
                                        <small className="text-muted d-block">
                                          Pagado: {f.pagado.detallePrograma.length === 0 ? 'nada' : f.pagado.detallePrograma.map(x => `${x.mes} ${formatCurrency(x.monto)}`).join(' · ')}
                                        </small>
                                        {conPrograma && f.programa.meses.length > 0 && (
                                          <><h6 className="mt-3 mb-2">Programa</h6>
                                            <ul className="small mb-0 ps-3">{f.programa.meses.map(m => <li key={m.mes}>{m.mes}: falta {formatCurrency(m.falta)}</li>)}</ul></>
                                        )}
                                      </Col>
                                      <Col md={5}>
                                        <h6 className="mb-2"><i className="fas fa-file-invoice me-1 text-primary"></i>Préstamos activos</h6>
                                        {f.prestamos.length === 0 ? <small className="text-muted">Sin préstamos activos</small> : (
                                          <Table size="sm" bordered className="mb-0 small bg-white">
                                            <thead><tr><th>Solicitud</th><th className="text-end">Capital que falta</th><th className="text-end">Interés vencido</th><th className="text-end">Debe hoy</th></tr></thead>
                                            <tbody>
                                              {f.prestamos.map(p => (
                                                <React.Fragment key={p.numeroSolicitud}>
                                                  <tr>
                                                    <td>{p.numeroSolicitud}<div className="text-muted">de {formatCurrency(p.monto)} en {p.plazo} cuotas</div></td>
                                                    <td className="text-end">{formatCurrency(p.capitalPendiente)}</td>
                                                    <td className="text-end">{formatCurrency(p.interesVencido)}</td>
                                                    <td className="text-end fw-bold">{formatCurrency(p.saldoTotal)}</td>
                                                  </tr>
                                                  <tr>
                                                    <td colSpan={4} className="text-muted">
                                                      Préstamo de {formatCurrency(p.monto)}: ya pagó {formatCurrency(p.capitalPagado)} de capital, le faltan {formatCurrency(p.capitalPendiente)}
                                                      {p.interesVencido > 0.009 && <> + {formatCurrency(p.interesVencido)} de interés vencido</>}
                                                      {' '}= <strong>debe {formatCurrency(p.saldoTotal)}</strong>.
                                                      {p.capitalAtrasado > 0.009
                                                        ? <> Va atrasado {formatCurrency(p.capitalAtrasado)} de capital respecto a su calendario ({p.cuotasVencidas} cuota(s) de {formatCurrency(p.capitalMensual)}).</>
                                                        : <> Va al día con sus cuotas.</>}
                                                    </td>
                                                  </tr>
                                                </React.Fragment>
                                              ))}
                                            </tbody>
                                          </Table>
                                        )}
                                      </Col>
                                      <Col md={3}>
                                        <h6 className="mb-2"><i className="fas fa-gavel me-1 text-secondary"></i>Multas pendientes</h6>
                                        {f.multas.length === 0 ? <small className="text-muted">Ninguna</small> : (
                                          <ul className="small mb-0 ps-3">{f.multas.map((m, i) => <li key={i}>{formatCurrency(m.monto)} — {m.motivo}</li>)}</ul>
                                        )}
                                        <Button size="sm" variant={f.exentoAportes ? 'outline-primary' : 'outline-secondary'} className="mt-3"
                                          disabled={guardandoExento === f.socioId}
                                          onClick={(e) => { e.stopPropagation(); alternarExento(f); }}>
                                          {guardandoExento === f.socioId
                                            ? <Spinner animation="border" size="sm" />
                                            : f.exentoAportes ? 'Quitar exención de aportes' : 'Marcar exento de aportes'}
                                        </Button>
                                      </Col>
                                    </Row>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="table-light fw-bold">
                          <td></td>
                          <td>Total ({incluidas.length} socios)</td>
                          <td className="text-end">{formatCurrency(totales.aportes)}</td>
                          {conPaseo && <td className="text-end">{formatCurrency(totales.paseo)}</td>}
                          {conPrograma && <td className="text-end">{formatCurrency(totales.programa)}</td>}
                          <td className="text-end">{formatCurrency(totales.prestamoMonto)}</td>
                          <td className="text-end">{formatCurrency(totales.prestamoSaldo)}</td>
                          <td className="text-end">{formatCurrency(totales.multas)}</td>
                          <td className="text-end text-danger">{formatCurrency(totales.totalAdeudado)}</td>
                          <td className="text-end text-success border-start">{formatCurrency(totales.paseoPagado)}</td>
                          <td className="text-end text-success">{formatCurrency(totales.programaPagado)}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                )}
                <small className="text-muted d-block mt-2">
                  <strong>Debe del préstamo hoy</strong> = capital que le falta pagar + interés vencido hasta este mes (lo mismo que
                  "Adeudado con interés" en su cronograma). <strong>Total que debe hoy</strong> = aportes{conPaseo ? ' + paseo' : ''}{conPrograma ? ' + programa' : ''} + préstamo + multas.
                  Toca un socio para ver el detalle.
                </small>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </Container>
  );
};

export default ReporteDeudas;
